import { Component } from "react";

// App-wide safety net: any uncaught render/runtime error (including an API/gateway
// timeout that bubbles up) is caught here and shown as a clean recoverable screen
// instead of a hard black crash.
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || "Something went wrong." };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, message: "" });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          data-testid="app-error-boundary"
          className="min-h-screen bg-[#050505] flex items-center justify-center px-6"
        >
          <div className="max-w-md w-full border border-white/10 bg-[#0A0A0A] p-8 text-center">
            <div className="font-serif text-2xl text-[#EDEDED] mb-3">
              Reel<span className="text-crimson">Vault</span>
            </div>
            <p className="text-white/70 text-sm mb-2">We hit a snag loading this view.</p>
            <p className="font-mono text-[11px] text-white/35 mb-6">
              Any background sync you started is still running on the server — this is just a
              display hiccup.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                data-testid="error-boundary-retry"
                onClick={this.handleReset}
                className="border border-amber bg-amber/10 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-amber hover:bg-amber hover:text-black transition-colors duration-300"
              >
                Try Again
              </button>
              <button
                data-testid="error-boundary-reload"
                onClick={() => window.location.reload()}
                className="border border-white/15 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-white/70 hover:border-white/40 transition-colors duration-300"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
